import {
	useContext,
	useEffect,
	useRef,
	useState,
	type ClipboardEvent,
	type FormEvent,
	type KeyboardEvent,
	type MouseEvent,
	type SyntheticEvent,
} from "react";
import type { AutocompleteChangeReason, AutocompleteInputChangeReason } from "@mui/material";
import { jobsNamed, parseJobReference, type Job, type JobReference } from "@domain/CRM/job";
import type { SearchResult } from "@domain/Search";
import { UserContext } from "@gui/contexts/user/UserContext";
import { API } from "@gui/infrastructure/pass-through/api";
import { Oops } from "@gui/infrastructure/pass-through/oops";
import { useJobLookupHighlight } from "./JobLookupHighlight.vm";
import { useJobLookupSearch } from "./JobLookupSearch.vm";

export interface JobLookupProps {
	value: string;
	onValueChange: (value: string) => void;
	/** Takes the job the lookup found. May be async; the box stays busy until it settles. */
	onFound: (job: Job) => void | Promise<void>;
	/** Takes the error, for a host that says it somewhere else than under the box. */
	onErrorChange?: (error: string | null) => void;
	/** Takes a chosen job's id instead of the box reading the job, for a host that only opens it. */
	onJobIdChosen?: (id: string) => void;
	/** A record id, link or job name pasted over the whole box is taken at once, with no Enter. */
	submitOnPaste?: boolean;
	/** Show the highlighted job's details in a card beside the list, from what the search returned. */
	showDetails?: boolean;
}

/** What Enter can look up. Plain text is the search's to answer, and an `invalid` is said, not read. */
type Findable = Exclude<JobReference, { kind: "text" }>;
type Lookup = Exclude<Findable, { kind: "invalid" }>;

const UNSEARCHABLE = "Type a field, grower or company, or paste a record ID or link.";

/** The ids the index holds under a name, which it answers from memory. Null when it has no answer: a job newer than
 *  its last poll, a warming index, a name past the endpoint's bounds, or a failed request. */
async function indexedIds(name: string): Promise<string[] | null> {
	try {
		const { ready, ids } = await API.Search.byName(name);
		return ready && ids.length > 0 ? ids : null;
	} catch {
		return null;
	}
}

/** The job with this name: its id if the index knew it, else the job, read live through the jobs filter and matched
 *  whole on this side too — the offline fake ignores filters. A name held twice (an archived skip and its continuation,
 *  say) is a question for the operator, not a coin toss. */
async function findNamed(name: string): Promise<Job | string> {
	const ids = await indexedIds(name);
	const jobs = ids ? [] : jobsNamed(await API.Data.Jobs.get.byFilter({ fields: { name } }), name);
	const matches = ids ?? jobs.map((job) => job.id);
	if (matches.length === 1) return jobs[0] ?? matches[0];
	if (matches.length === 0) throw new Error(`No job is named "${name}".`);
	const listed = matches.join(", ");
	throw new Error(`${matches.length} jobs are named that (${listed}) — paste the record ID of the one you mean.`);
}

const describeFailure = (err: unknown, ref: Lookup): string => {
	const message = err instanceof Error ? err.message : String(err);
	if (ref.kind === "id" && Oops.isNotFound(err)) return `Job not found: ${ref.id}`;
	return message || "Failed to load the job.";
};

export const useJobLookupViewModel = ({
	value,
	onValueChange,
	onFound,
	onErrorChange,
	onJobIdChosen,
	submitOnPaste = false,
	showDetails = false,
}: JobLookupProps) => {
	const user = useContext(UserContext);
	const [loading, setLoading] = useState(false);
	// What the operator typed, as distinct from `value`, which a host may fill: a box the route
	// filled is no search.
	const [typed, setTyped] = useState("");
	// Enter on text before its rows arrived: the top row is taken once they do.
	const [enterPending, setEnterPending] = useState(false);
	// A row reached by the arrow keys is what Enter means, even on a job's name.
	const movedToRow = useRef(false);
	const search = useJobLookupSearch({ text: typed === value ? typed : "", enabled: Boolean(user?.isAdmin) });
	const highlight = useJobLookupHighlight({ options: search.options, open: search.dropdownOpen });
	const [error, setErrorState] = useState<string | null>(null);
	const setError = (next: string | null) => {
		setErrorState(next);
		onErrorChange?.(next);
	};

	// A lookup outlives its box when the host remounts it, as the toolbar does on every route: what it finds then is
	// no longer wanted, and handing it over could move someone off the page they went to. Set here as well as cleared
	// in the cleanup, because StrictMode runs this effect mount, cleanup, mount.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const edit = (next: string) => {
		setTyped(next);
		setEnterPending(false);
		movedToRow.current = false;
		onValueChange(next);
		setError(null);
		search.reopen();
		// MUI moves its highlight to the top row on any change of text, and doesn't report it.
		highlight.reset();
	};
	const clear = () => edit("");

	// A name the index answered arrives as an id, which is all a host that only opens the job needs.
	const handOver = async (found: Job | string) => {
		if (typeof found !== "string") return onFound(found);
		if (onJobIdChosen) return onJobIdChosen(found);
		const job = await API.Data.Jobs.get.byId(found, false);
		if (mounted.current) await onFound(job);
	};

	const lookUp = async (ref: Findable) => {
		if (ref.kind === "invalid") {
			setError(ref.reason);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const found = ref.kind === "id" ? await API.Data.Jobs.get.byId(ref.id, false) : await findNamed(ref.name);
			if (mounted.current) await handOver(found);
		} catch (err) {
			if (mounted.current) setError(describeFailure(err, ref));
		} finally {
			setLoading(false);
		}
	};

	const chooseId = (id: string) => {
		if (onJobIdChosen) onJobIdChosen(id);
		else void lookUp({ kind: "id", id });
	};

	useEffect(() => {
		if (!enterPending || !search.settled) return;
		setEnterPending(false);
		const [top] = search.options;
		if (!top) return;
		// Closed first: taking the job may disable the box, and MUI closing it mid-render warns.
		search.dismiss();
		chooseId(top.id);
		// On the answer landing alone: `chooseId` is rebuilt every render.
	}, [enterPending, search.settled]);

	// Text never goes to Airtable: rows or a notice are the search's answer, on screen or on the way.
	const answerEnterOnText = () => {
		search.reopen();
		if (!search.settled) setEnterPending(true);
		else if (search.options.length === 0 && search.notice === null) setError(UNSEARCHABLE);
	};

	const submit = (event: FormEvent) => {
		event.preventDefault();
		const ref = parseJobReference(value);
		if (ref.kind === "text") {
			answerEnterOnText();
			return;
		}
		search.dismiss();
		// The submit button stays pressable while busy (BusyButton), so a second press lands here.
		if (loading) return;
		// A blank box gets no lecture — the button is disabled for it anyway.
		if (!value.trim()) return;
		if (ref.kind === "id") chooseId(ref.id);
		else void lookUp(ref);
	};

	// MUI writes to the input on its own account too: a reset on mount, a picked row's title. The box only follows
	// the operator.
	const onInputChange = (_event: SyntheticEvent, next: string, reason: AutocompleteInputChangeReason) => {
		if (reason === "input") edit(next);
	};

	// Enter with no row arrives as a `createOption`, and goes on to the form's submit.
	const onOptionChosen = (
		_event: SyntheticEvent,
		option: SearchResult | string | null,
		reason: AutocompleteChangeReason,
	) => {
		if (reason === "selectOption" && option !== null && typeof option !== "string") chooseId(option.id);
	};

	// Rows on screen for older text are not what Enter means, and nor is the top row on a job's whole name: rows match
	// words, so `F1` finds `F10`, and the answer would hang on whether they had landed. MUI steps aside for both, and the
	// form's submit answers for what is in the box.
	const onKeyDown = (event: KeyboardEvent<HTMLDivElement> & { defaultMuiPrevented?: boolean }) => {
		if (event.key === "ArrowDown" || event.key === "ArrowUp") movedToRow.current = true;
		if (event.key !== "Enter") return;
		const exactName = parseJobReference(value).kind === "name" && !movedToRow.current;
		if (!search.settled || exactName) event.defaultMuiPrevented = true;
	};

	const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
		if (!submitOnPaste) return;
		const box = event.currentTarget;
		const replacesAll = box.selectionStart === 0 && box.selectionEnd === box.value.length;
		const text = event.clipboardData.getData("text");
		const ref = parseJobReference(text);
		if (!replacesAll || (ref.kind !== "id" && ref.kind !== "name")) return;
		event.preventDefault();
		edit(text);
		if (ref.kind === "id") {
			chooseId(ref.id);
			return;
		}
		search.dismiss();
		void lookUp(ref);
	};

	// A click in the pane must not take focus from the box: the blur would close the dropdown it is in.
	const onPaneMouseDown = (event: MouseEvent) => event.preventDefault();

	return {
		value,
		edit,
		clear,
		submit,
		lookUp,
		loading,
		error,
		canSubmit: !loading && value.trim() !== "",
		options: search.options,
		notice: search.notice,
		noticeState: search.noticeState,
		dropdownOpen: search.dropdownOpen,
		onFocus: search.onFocus,
		onBlur: () => {
			setEnterPending(false);
			search.onBlur();
		},
		onOpen: search.reopen,
		onClose: () => {
			setEnterPending(false);
			search.dismiss();
		},
		onInputChange,
		onOptionChosen,
		onKeyDown,
		handlePaste,
		onHighlightChange: showDetails ? highlight.onHighlightChange : undefined,
		paneRow: showDetails ? highlight.row : null,
		onPaneMouseDown: showDetails ? onPaneMouseDown : undefined,
	};
};
